// src/routes/AppRouter.jsx
import {
  createBrowserRouter,
  RouterProvider,
  useNavigate,
  Navigate,
} from 'react-router-dom';
import { Suspense, lazy } from 'react';

// Layouts
import { MainLayout } from '@/components/layout/MainLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

// Guards de rota
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import PublicRoute from '@/components/auth/PublicRoute';

import { PageLoader } from '@/components/ui/PageLoader';

// ==================== CONSTANTES DE ROTAS ====================
// Declaradas ANTES de useAppNavigation e do router para evitar
// ReferenceError (const não sofre hoisting como var/function).
export const ROUTES = {
  // Públicas
  HOME: '/',
  ABOUT: '/about',
  CONTACT: '/contact',
  PRICING: '/pricing',
  FIND_SUPERVISOR: '/find-supervisor',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  LGPD: '/lgpd',
  CODIGO_ETICA: '/codigo-etica',

  // Autenticação
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  AUTH_CALLBACK: '/auth/callback', // ✅ callback OAuth Google/Facebook
  COMPLETE_PROFILE: '/complete-profile', // ✅ escolha de perfil pós-OAuth

  // Dashboard
  DASHBOARD: '/dashboard',
  DASHBOARD_PSICOLOGO: '/dashboard/psicologo',
  DASHBOARD_SUPERVISOR: '/dashboard/supervisor',
  // DASHBOARD_INSTITUICAO: '/dashboard/instituicao', // DESABILITADO
  DASHBOARD_INTERNACIONAL: '/dashboard/internacional',

  // Agenda Digital
  AGENDA_CRIAR: '/dashboard/agenda-digital/criar',
  AGENDA_GERENCIAR: '/dashboard/agenda-digital/gerenciar',
  AGENDA_CONFIGURAR: '/dashboard/agenda-digital/configurar',
  AGENDA_ESTATISTICAS: '/dashboard/agenda-digital/estatisticas',

  // Supervisão
  SUPERVISAO_BUSCAR: '/dashboard/supervisao/buscar',
  SUPERVISAO_MINHAS: '/dashboard/supervisao/minhas',
  SUPERVISAO_MATCHING: '/dashboard/supervisao/matching',
  // Uso: navigate(`${ROUTES.SUPERVISAO_AGENDAR}/${supervisorId}`)
  SUPERVISAO_AGENDAR: '/dashboard/supervisao/agendar',
  SUPERVISAO_CONFIGURAR: '/dashboard/supervisao/configurar',
  // Uso: navigate(`${ROUTES.SUPERVISAO_PERFIL}/${id}`)
  SUPERVISAO_PERFIL: '/dashboard/supervisao/supervisor',

  // Supervisor
  MEUS_SUPERVISIONADOS: '/dashboard/supervisor/supervisionados',
  BUSCAR_SUPERVISIONADOS: '/dashboard/supervisor/encontrar-supervisionados',

  // Configurações
  PERFIL: '/dashboard/perfil',
  CONFIGURACOES: '/dashboard/configuracoes',
  NOTIFICACOES: '/dashboard/notificacoes',
  PAGAMENTOS: '/dashboard/pagamentos',

  // Instituição — sub-páginas (DESABILITADO: perfil instituição não disponível por enquanto)
  // INSTITUICAO_PROFISSIONAIS: '/dashboard/instituicao/profissionais',
  // INSTITUICAO_SUPERVISOES: '/dashboard/instituicao/supervisoes',
  // INSTITUICAO_RELATORIOS: '/dashboard/instituicao/relatorios',
  // INSTITUICAO_PLANOS: '/dashboard/instituicao/planos',

  // Pagamento
  SUBSCRIPTION: '/subscription',
  PAYMENT_SUCCESS: '/payment/success',
  PAYMENT_FAILURE: '/payment/failure',
  PAYMENT_PENDING: '/payment/pending',

  // Administração
  ADMIN_LOGIN: '/admin/login',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_REVENUE: '/admin/analytics/revenue',
  ADMIN_SUBSCRIPTIONS: '/admin/analytics/subscriptions',
  ADMIN_FOOTER_SETTINGS: '/admin/footer-settings',
};

// ==================== LAZY IMPORTS ====================

// === ROTAS PÚBLICAS ===
const HomePage = lazy(() => import('@/pages/public/HomePage'));
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const PricingPage = lazy(() => import('@/pages/public/PricingPage'));
const NotFoundPage = lazy(() => import('@/pages/public/NotFoundPage'));
const PrivacyPage = lazy(() => import('@/pages/public/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/public/TermsPage'));
const LgpdPage = lazy(() => import('@/pages/public/LgpdPage'));
const CodigoEticaPage = lazy(() => import('@/pages/public/CodigoEticaPage'));

// === AGENDA DIGITAL — PÚBLICAS (sem autenticação) ===
const ConsultorioPublico = lazy(() => import('@/pages/agenda-digital/public/ConsultorioPublico'));
const AgendamentoPaciente = lazy(() => import('@/pages/agenda-digital/public/AgendamentoPaciente'));
const ConfirmacaoAgendamento = lazy(() => import('@/pages/agenda-digital/public/ConfirmacaoAgendamento'));
const StatusAgendamento = lazy(() => import('@/pages/agenda-digital/public/StatusAgendamento'));

// === AUTENTICAÇÃO ===
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage')); // ✅ callback OAuth
const CompleteProfilePage = lazy(() => import('@/pages/auth/CompleteProfilePage')); // ✅ perfil pós-OAuth

// === DASHBOARD ===
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const PsicologoDashboard = lazy(() => import('@/pages/dashboard/psicologo/PsicologoDashboard'));
const SupervisorDashboard = lazy(() => import('@/pages/dashboard/supervisor/SupervisorDashboard'));
// === INSTITUIÇÃO — DESABILITADO (perfil instituição não disponível por enquanto) ===
// const InstituicaoDashboard = lazy(() => import('@/pages/dashboard/instituicao/InstituicaoDashboard'));
// const ProfissionaisInstituicaoPage = lazy(() => import('@/pages/instituicao/profissionais/ProfissionaisInstituicaoPage'));
// const SupervisoesInstituicaoPage = lazy(() => import('@/pages/instituicao/supervisoes/SupervisoesInstituicaoPage'));
// const RelatoriosInstituicaoPage = lazy(() => import('@/pages/instituicao/relatorios/RelatoriosInstituicaoPage'));
const InternacionalDashboard = lazy(() => import('@/pages/dashboard/internacional/InternacionalDashboard'));

// === AGENDA DIGITAL — PRIVADAS ===
const CriarAgendaPage = lazy(() => import('@/pages/agenda-digital/criar/CriarAgendaPage'));
const GerenciarConsultasPage = lazy(() => import('@/pages/agenda-digital/gerenciar/GerenciarConsultasPage'));
const ConfigurarAgendaPage = lazy(() => import('@/pages/agenda-digital/configurar/ConfigurarAgendaPage'));
const EstatisticasAgendaPage = lazy(() => import('@/pages/agenda-digital/estatisticas/EstatisticasAgendaPage'));

// === SUPERVISÃO CLÍNICA ===
const BuscarSupervisorPage = lazy(() => import('@/pages/supervisao/buscar/BuscarSupervisorPage'));
const AgendarSupervisaoPage = lazy(() => import('@/pages/supervisao/agendar/AgendarSupervisaoPage'));
const MinhasSupervisoesPage = lazy(() => import('@/pages/supervisao/minhas/MinhasSupervisoesPage'));
const MatchingSupervisaoPage = lazy(() => import('@/pages/supervisao/matching/MatchingSupervisaoPage'));
const PerfilSupervisorPage = lazy(() => import('@/pages/supervisao/perfil/PerfilSupervisorPage'));
const MeusSupervisionadosPage = lazy(() => import('@/pages/supervisao/gerenciar/MeusSupervisionadosPage'));
const EncontrarSupervisionadosPage = lazy(() => import('@/pages/supervisao/gerenciar/EncontrarSupervisionadosPage'));
const ConfigurarSupervisaoPage = lazy(() => import('@/pages/supervisao/configurar/ConfigurarSupervisaoPage'));

// === CONFIGURAÇÕES ===
const PerfilUsuarioPage = lazy(() => import('@/pages/configuracoes/PerfilUsuarioPage'));
const ConfiguracoesContaPage = lazy(() => import('@/pages/configuracoes/ConfiguracoesContaPage'));
const NotificacoesPage = lazy(() => import('@/pages/configuracoes/NotificacoesPage'));
const PagamentosPage = lazy(() => import('@/pages/configuracoes/PagamentosPage'));

// === PAGAMENTO — callbacks públicos (MercadoPago/PagSeguro) ===
const SubscriptionPage = lazy(() => import('@/pages/payment/SubscriptionPage'));
const PaymentSuccessPage = lazy(() => import('@/pages/payment/PaymentSuccessPage'));
const PaymentFailurePage = lazy(() => import('@/pages/payment/PaymentFailurePage'));
const PaymentPendingPage = lazy(() => import('@/pages/payment/PaymentPendingPage'));

// === ADMINISTRAÇÃO ===
const AdminLoginPage = lazy(() => import('@/pages/admin/AdminLoginPage'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const RevenueAnalytics = lazy(() => import('@/pages/admin/analytics/RevenueAnalytics'));
const SubscriptionAnalytics = lazy(() => import('@/pages/admin/analytics/SubscriptionAnalytics'));
const FooterSettingsPage = lazy(() => import('@/pages/admin/footerSettings/FooterSettingsPage'));

// ==================== SUSPENSE WRAPPER ====================
const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

// ==================== CONFIGURAÇÃO DAS ROTAS ====================
//
// Grupos:
//  🔓 PÚBLICAS       — acessíveis sem autenticação
//  🔐 AUTENTICAÇÃO   — bloqueadas se já logado (PublicRoute)
//  🔒 PRIVADAS       — bloqueadas se não logado (ProtectedRoute)
//  🛡️ ADMINISTRAÇÃO  — acesso restrito a administradores
//
const router = createBrowserRouter(
  [
    // =========================================================
    // 🔓 ROTAS PÚBLICAS
    // =========================================================
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { index: true, element: <SuspenseWrapper><HomePage /></SuspenseWrapper> },
        { path: 'about', element: <SuspenseWrapper><AboutPage /></SuspenseWrapper> },
        { path: 'contact', element: <SuspenseWrapper><ContactPage /></SuspenseWrapper> },
        { path: 'pricing', element: <SuspenseWrapper><PricingPage /></SuspenseWrapper> },
        { path: 'privacy', element: <SuspenseWrapper><PrivacyPage /></SuspenseWrapper> },
        { path: 'terms', element: <SuspenseWrapper><TermsPage /></SuspenseWrapper> },
        { path: 'lgpd', element: <SuspenseWrapper><LgpdPage /></SuspenseWrapper> },
        { path: 'codigo-etica', element: <SuspenseWrapper><CodigoEticaPage /></SuspenseWrapper> },
        // find-supervisor é pública (landing de busca sem dados protegidos)
        { path: 'find-supervisor', element: <SuspenseWrapper><BuscarSupervisorPage /></SuspenseWrapper> },
      ],
    },

    // Agenda Digital — perfis públicos de consultório
    {
      path: '/consultorio/:slug',
      element: <SuspenseWrapper><ConsultorioPublico /></SuspenseWrapper>,
    },
    {
      path: '/consultorio/:slug/agendar',
      element: <SuspenseWrapper><AgendamentoPaciente /></SuspenseWrapper>,
    },
    {
      path: '/agendamento/:id/confirmacao',
      element: <SuspenseWrapper><ConfirmacaoAgendamento /></SuspenseWrapper>,
    },
    {
      path: '/agendamento/:id/status',
      element: <SuspenseWrapper><StatusAgendamento /></SuspenseWrapper>,
    },

    // Pagamento — callbacks de gateway (sem sessão obrigatória)
    {
      path: '/subscription',
      element: <SuspenseWrapper><SubscriptionPage /></SuspenseWrapper>,
    },
    {
      path: '/payment/success',
      element: <SuspenseWrapper><PaymentSuccessPage /></SuspenseWrapper>,
    },
    {
      path: '/payment/failure',
      element: <SuspenseWrapper><PaymentFailurePage /></SuspenseWrapper>,
    },
    {
      path: '/payment/pending',
      element: <SuspenseWrapper><PaymentPendingPage /></SuspenseWrapper>,
    },

    // =========================================================
    // ✅ ROTA DE CALLBACK OAUTH (Google / Facebook)
    // Deve ficar FORA do PublicRoute para funcionar mesmo quando
    // o Supabase ainda não processou a sessão completamente.
    // A AuthCallbackPage aguarda o AuthContext resolver e redireciona.
    // =========================================================
    {
      path: '/auth/callback',
      element: <SuspenseWrapper><AuthCallbackPage /></SuspenseWrapper>,
    },

    // ✅ COMPLETAR PERFIL PÓS-OAUTH
    {
      path: "/complete-profile",
      element: <SuspenseWrapper><CompleteProfilePage /></SuspenseWrapper>,
    },

    // =========================================================
    // 🔐 ROTAS DE AUTENTICAÇÃO
    // Bloqueadas para usuários já autenticados → redirect /dashboard
    // =========================================================
    {
      path: '/',
      element: (
        <PublicRoute>
          <AuthLayout />
        </PublicRoute>
      ),
      children: [
        { path: 'login', element: <SuspenseWrapper><LoginPage /></SuspenseWrapper> },
        // Aceita ?ref=TOKEN para convites (featureInviteService)
        { path: 'register', element: <SuspenseWrapper><RegisterPage /></SuspenseWrapper> },
        { path: 'forgot-password', element: <SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper> },
        { path: 'reset-password', element: <SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper> },
      ],
    },

    // =========================================================
    // 🔒 ROTAS PRIVADAS
    // Bloqueadas para não autenticados → redirect /login
    // =========================================================
    {
      path: '/dashboard',
      element: (
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        // Index: DashboardPage decide qual sub-dashboard renderizar
        // com base no user_type — sem redirect automático cego.
        // IMPORTANTE: DashboardPage deve aguardar isLoading=false antes
        // de qualquer navigate() para evitar loop de redirect.
        {
          index: true,
          element: <SuspenseWrapper><DashboardPage /></SuspenseWrapper>,
        },

        // Sub-dashboards por tipo de usuário
        {
          path: 'psicologo',
          element: <SuspenseWrapper><PsicologoDashboard /></SuspenseWrapper>,
        },
        {
          path: 'supervisor',
          children: [
            {
              index: true,
              element: <SuspenseWrapper><SupervisorDashboard /></SuspenseWrapper>,
            },
            // /dashboard/supervisor/supervisionados → lista de supervisionados ativos
            {
              path: 'supervisionados',
              element: <SuspenseWrapper><MeusSupervisionadosPage /></SuspenseWrapper>,
            },
            // /dashboard/supervisor/encontrar-supervisionados → busca ativa de supervisionados
            {
              path: 'encontrar-supervisionados',
              element: <SuspenseWrapper><EncontrarSupervisionadosPage /></SuspenseWrapper>,
            },
          ],
        },
        // === Instituição (DESABILITADO: perfil instituição não disponível por enquanto) ===
        // {
        //   path: 'instituicao',
        //   children: [
        //     {
        //       index: true,
        //       element: <SuspenseWrapper><InstituicaoDashboard /></SuspenseWrapper>,
        //     },
        //     {
        //       path: 'profissionais',
        //       element: <SuspenseWrapper><ProfissionaisInstituicaoPage /></SuspenseWrapper>,
        //     },
        //     {
        //       path: 'supervisoes',
        //       element: <SuspenseWrapper><SupervisoesInstituicaoPage /></SuspenseWrapper>,
        //     },
        //     {
        //       path: 'relatorios',
        //       element: <SuspenseWrapper><RelatoriosInstituicaoPage /></SuspenseWrapper>,
        //     },
        //     // /dashboard/instituicao/planos — referenciado em InstituicaoDashboard e InternacionalDashboard
        //     {
        //       path: 'planos',
        //       element: <Navigate to="/subscription" replace />,
        //     },
        //   ],
        // },
        {
          path: 'internacional',
          element: <SuspenseWrapper><InternacionalDashboard /></SuspenseWrapper>,
        },

        // === Agenda Digital (privada) ===
        {
          path: 'agenda-digital',
          children: [
            // /dashboard/agenda-digital → redireciona para gerenciar
            {
              index: true,
              element: <Navigate to="gerenciar" replace />,
            },
            {
              path: 'criar',
              element: <SuspenseWrapper><CriarAgendaPage /></SuspenseWrapper>,
            },
            {
              path: 'gerenciar',
              element: <SuspenseWrapper><GerenciarConsultasPage /></SuspenseWrapper>,
            },
            {
              path: 'configurar',
              element: <SuspenseWrapper><ConfigurarAgendaPage /></SuspenseWrapper>,
            },
            {
              path: 'estatisticas',
              element: <SuspenseWrapper><EstatisticasAgendaPage /></SuspenseWrapper>,
            },
          ],
        },

        // === Supervisão Clínica (privada) ===
        {
          path: 'supervisao',
          children: [
            // /dashboard/supervisao → redireciona para minhas
            {
              index: true,
              element: <Navigate to="minhas" replace />,
            },
            {
              path: 'buscar',
              element: <SuspenseWrapper><BuscarSupervisorPage /></SuspenseWrapper>,
            },
            {
              path: 'agendar/:supervisorId',
              element: <SuspenseWrapper><AgendarSupervisaoPage /></SuspenseWrapper>,
            },
            {
              path: 'minhas',
              element: <SuspenseWrapper><MinhasSupervisoesPage /></SuspenseWrapper>,
            },
            {
              path: 'matching',
              element: <SuspenseWrapper><MatchingSupervisaoPage /></SuspenseWrapper>,
            },
            {
              path: 'supervisor/:id',
              element: <SuspenseWrapper><PerfilSupervisorPage /></SuspenseWrapper>,
            },
            {
              path: 'configurar',
              element: <SuspenseWrapper><ConfigurarSupervisaoPage /></SuspenseWrapper>,
            },
          ],
        },

        // Aliases de retrocompatibilidade
        // Mantido para navigate('/dashboard/meus-supervisionados') legado
        {
          path: 'meus-supervisionados',
          element: <Navigate to="/dashboard/supervisor/supervisionados" replace />,
        },
        // Mantido para links antigos apontando para configurar-supervisao
        {
          path: 'configurar-supervisao',
          element: <Navigate to="/dashboard/supervisao/configurar" replace />,
        },

        // === Configurações ===
        {
          path: 'perfil',
          element: <SuspenseWrapper><PerfilUsuarioPage /></SuspenseWrapper>,
        },
        {
          path: 'configuracoes',
          element: <SuspenseWrapper><ConfiguracoesContaPage /></SuspenseWrapper>,
        },
        {
          path: 'notificacoes',
          element: <SuspenseWrapper><NotificacoesPage /></SuspenseWrapper>,
        },
        {
          path: 'pagamentos',
          element: <SuspenseWrapper><PagamentosPage /></SuspenseWrapper>,
        },
      ],
    },

    // =========================================================
    // 🛡️ ROTAS ADMINISTRATIVAS
    // /admin/* — acesso restrito a administradores da plataforma
    // =========================================================
    {
      path: '/admin/login',
      element: <SuspenseWrapper><AdminLoginPage /></SuspenseWrapper>,
    },
    {
      path: '/admin',
      element: (
        <ProtectedRoute adminOnly>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="dashboard" replace />,
        },
        {
          path: 'dashboard',
          element: <SuspenseWrapper><AdminDashboard /></SuspenseWrapper>,
        },
        {
          path: 'analytics',
          children: [
            {
              index: true,
              element: <Navigate to="revenue" replace />,
            },
            {
              path: 'revenue',
              element: <SuspenseWrapper><RevenueAnalytics /></SuspenseWrapper>,
            },
            {
              path: 'subscriptions',
              element: <SuspenseWrapper><SubscriptionAnalytics /></SuspenseWrapper>,
            },
          ],
        },
        {
          path: 'footer-settings',
          element: <SuspenseWrapper><FooterSettingsPage /></SuspenseWrapper>,
        },
      ],
    },

    // =========================================================
    // 404
    // =========================================================
    {
      path: '*',
      element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper>,
    },
  ],
  {
    // Flags válidas para React Router DOM 6.20.
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
    },
  }
);

// ==================== COMPONENTE PRINCIPAL ====================
export const AppRouter = () => (
  <RouterProvider router={router} fallbackElement={<PageLoader />} />
);

// ==================== HOOK DE NAVEGAÇÃO ====================
// useAppNavigation deve ser usado SOMENTE dentro de componentes
// filhos do RouterProvider. useNavigate() fora desse contexto lança erro:
// "useNavigate() may be used only in the context of a Router component."
export const useAppNavigation = () => {
  const navigate = useNavigate();

  return {
    navigate,

    navigateToDashboard: (userType) => {
      const routes = {
        psicologo: ROUTES.DASHBOARD_PSICOLOGO,
        supervisor: ROUTES.DASHBOARD_SUPERVISOR,
        // instituicao: ROUTES.DASHBOARD_INSTITUICAO, // DESABILITADO
        internacional: ROUTES.DASHBOARD_INTERNACIONAL,
      };
      navigate(routes[userType] ?? ROUTES.DASHBOARD);
    },

    navigateToAgendaDigital: () => navigate(ROUTES.AGENDA_GERENCIAR),
    navigateToSupervisao: () => navigate(ROUTES.SUPERVISAO_MINHAS),
    navigateToConsultorio: (slug) => navigate(`/consultorio/${slug}`),

    navigateToLogin: () => navigate(ROUTES.LOGIN),
    navigateToRegister: () => navigate(ROUTES.REGISTER),

    navigateToSubscription: () => navigate(ROUTES.SUBSCRIPTION),
    navigateToPaymentSuccess: (state) => navigate(ROUTES.PAYMENT_SUCCESS, { state }),
    navigateToPaymentFailure: (state) => navigate(ROUTES.PAYMENT_FAILURE, { state }),
    navigateToPaymentPending: (state) => navigate(ROUTES.PAYMENT_PENDING, { state }),

    navigateToAdminDashboard: () => navigate(ROUTES.ADMIN_DASHBOARD),
    // navigateToInstituicaoPlanos: () => navigate(ROUTES.SUBSCRIPTION), // DESABILITADO

    navigateToSupervisaoPerfil: (id) => navigate(`${ROUTES.SUPERVISAO_PERFIL}/${id}`),
    navigateToAgendarSupervisao: (supervisorId) => navigate(`${ROUTES.SUPERVISAO_AGENDAR}/${supervisorId}`),
  };
};

export default AppRouter;
